
import { Product, CartItem } from '../types';
import { productQueries } from '../../data/products';
import { productRepository } from '../../data/productRepository';
import { db } from '../../infrastructure/firebaseConfig';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { APP_CONFIG } from '../constants';
import { logger } from '../../infrastructure/logger';

export const cartService = {
    /**
     * Persists the current cart items to Firestore for a specific user.
     */
    async saveCartToCloud(userId: string, items: CartItem[]): Promise<void> {
        try {
            const cartRef = doc(db, 'carts', userId);
            await setDoc(cartRef, { items, updatedAt: new Date().toISOString() });
        } catch (error) {
            logger.error('[CartService] Cloud Cart Save Error', error);
        }
    },

    /**
     * Retrieves the persisted cart items from Firestore for a specific user.
     */
    async getCartFromCloud(userId: string): Promise<CartItem[]> {
        try {
            const cartRef = doc(db, 'carts', userId);
            const snap = await getDoc(cartRef);
            if (snap.exists()) {
                return snap.data().items as CartItem[];
            }
        } catch (error) {
            logger.error('[CartService] Cloud Cart Fetch Error', error);
        }
        return [];
    },

    /**
     * Validates if a product can be added to the cart based on real-time DB stock.
     */
    async validateAddToCart(product: Product, currentQuantityInCart: number, quantityToAdd: number): Promise<{ success: boolean; message?: string }> {
        try {
            // 1. Fetch fresh stock from DB (Simulated)
            const dbProduct = await productQueries.getProductById(product.id);
            
            if (!dbProduct) {
                return { success: false, message: 'Producto no disponible' };
            }

            // 2. Validate Stock
            if (currentQuantityInCart + quantityToAdd > dbProduct.stock) {
                return { success: false, message: `Stock insuficiente. Máximo disponible: ${dbProduct.stock}` };
            }

            // 3. Validate Policy Limits
            if (currentQuantityInCart + quantityToAdd > APP_CONFIG.MAX_QTY_PER_ITEM) {
                return { success: false, message: `Máximo ${APP_CONFIG.MAX_QTY_PER_ITEM} unidades por producto.` };
            }

            return { success: true };
        } catch (error) {
            logger.error('[CartService] Cart Validation Error', error);
            return { success: false, message: 'Error al validar stock' };
        }
    },

    /**
     * Validates all items in cart and returns adjusted items if stock changed.
     */
    async validateCartItems(items: CartItem[]): Promise<{ adjustedItems: CartItem[], changesMade: boolean }> {
        const adjustedItems: CartItem[] = [];
        let changesMade = false;

        for (const item of items) {
            const dbProduct = await productRepository.getProductById(item.product.id);
            if (!dbProduct || dbProduct.stock <= 0) {
                // Remove item if no stock
                changesMade = true;
                continue; 
            }

            if (item.quantity > dbProduct.stock) {
                // Adjust to max available
                adjustedItems.push({ ...item, quantity: dbProduct.stock, product: dbProduct });
                changesMade = true;
            } else {
                adjustedItems.push({ ...item, product: dbProduct });
            }
        }

        return { adjustedItems, changesMade };
    }
};
